import { useLocation } from "wouter";
import { getRouteKeyFromPath, getLocalizedPath } from "@/hooks/useLocalizedPath";
import blogData from "../../../blog-articles-data.json";

type Language = 'es' | 'en' | 'sv';

export default function LanguageSwitcher() {
  const [location, setLocation] = useLocation();
  
  const isEnglish = location.startsWith('/en');
  const isSwedish = location.startsWith('/sv');
  const isSpanish = !isEnglish && !isSwedish;

  const switchTo = (targetLang: Language) => {
    // Remove hash if present for lookup
    const [pathWithoutHash, hash] = location.split('#');
    
    // Check if this is a blog article page
    const blogMatch = pathWithoutHash.match(/^(?:\/(en|sv))?\/(?:blog|blogg)\/(.+)$/);
    if (blogMatch) {
      const slug = blogMatch[2];
      // Find the article by any of its slugs
      const articles = blogData as any[];
      const article = articles.find((a: any) =>
        a.slug === slug || a.slug_es === slug || a.slug_sv === slug
      );
      if (article) {
        let newPath: string;
        if (targetLang === 'es') {
          newPath = `/blog/${article.slug_es || article.slug}`;
        } else if (targetLang === 'sv') {
          newPath = `/sv/blogg/${article.slug_sv || article.slug}`;
        } else {
          newPath = `/en/blog/${article.slug}`;
        }
        if (hash) newPath += '#' + hash;
        setLocation(newPath);
        return;
      }
    }
    
    // Try to find the route key from the current path
    const result = getRouteKeyFromPath(pathWithoutHash);
    
    if (result) {
      let newPath = getLocalizedPath(result.routeKey, targetLang);
      if (hash) newPath += '#' + hash;
      setLocation(newPath);
    } else {
      // Fallback: strip current prefix and add new one
      let basePath = pathWithoutHash;
      if (isEnglish) basePath = pathWithoutHash.slice(3) || '/';
      else if (isSwedish) basePath = pathWithoutHash.slice(3) || '/';
      
      let newPath: string;
      if (targetLang === 'es') {
        newPath = basePath === '/' ? '/' : basePath;
      } else {
        newPath = `/${targetLang}${basePath === '/' ? '' : basePath}`;
      }
      if (hash) newPath += '#' + hash;
      setLocation(newPath);
    }
  };

  return (
    <div className="flex gap-1.5 items-center">
      <button
        onClick={() => switchTo('es')}
        className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition-all ${
          isSpanish
            ? 'bg-teal-600 text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        title="Español"
      >
        🇪🇸 ES
      </button>
      <button
        onClick={() => switchTo('en')}
        className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition-all ${
          isEnglish
            ? 'bg-teal-600 text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        title="English"
      >
        🇬🇧 EN
      </button>
      <button
        onClick={() => switchTo('sv')}
        className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition-all ${
          isSwedish
            ? 'bg-teal-600 text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        title="Svenska"
      >
        🇸🇪 SV
      </button>
    </div>
  );
}
